import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router';
import { AppShell } from './features/app/app-shell.js';
import { LandingPage } from './features/landing/landing-page.js';
import { DashboardPage } from './features/dashboard/dashboard-page.js';
import { BlogPage } from './features/blog/blog-page.js';
import { BlogPostPage } from './features/blog/blog-post-page.js';
import { DocsPage } from './features/docs/docs-page.js';
import { ChangelogPage } from './features/changelog/changelog-page.js';
import { FaqPage } from './features/faq/faq-page.js';
// Lazy-loaded so its Stripe imports (which auto-inject Stripe.js + fraud-detection
// scripts on module evaluation) stay off every other page and out of the
// pre-consent boot path. Stripe loads only when the user opens /billing (the
// payment surface, where the cookies are strictly necessary).
const BillingPage = lazy(() =>
  import('./features/billing/billing-page.js').then((m) => ({ default: m.BillingPage })),
);
import { SettingsPage } from './features/settings/settings-page.js';
import { NotFoundPage } from './features/not-found/not-found-page.js';
import { SignInPage } from './features/auth/sign-in-page.js';
import { SignUpPage } from './features/auth/sign-up-page.js';
import { SsoCallbackPage } from './features/auth/sso-callback-page.js';
import { ProtectedRoute } from './features/auth/protected-route.js';
import { DeviceAuthPage } from './features/auth/device-auth-page.js';
import { EditorLayout, EditorPage } from './features/editor/index.js';
import { GettingStartedPage } from './features/getting-started/getting-started-page.js';
import { CookieBanner } from './components/ui/cookie-banner/cookie-banner.js';
import { LegalPage } from './features/legal/legal-page.js';

export function AppRoutes() {
  return (
    <Routes>
      {/* Public — no AppShell (full-page Clerk UI) */}
      <Route path="/sign-in/*" element={<SignInPage />} />
      <Route path="/sign-up/*" element={<SignUpPage />} />
      <Route path="/sso-callback" element={<SsoCallbackPage />} />

      {/* Onboarding — merged into /getting-started; keep the URL as a redirect
          so old sign-up redirects and bookmarked links keep working. */}
      <Route path="/onboarding" element={<Navigate to="/getting-started" replace />} />

      {/* Editor — no AppShell, authenticates via signed URL */}
      <Route
        path="/editor/:sessionId"
        element={
          <EditorLayout>
            <EditorPage />
          </EditorLayout>
        }
      />

      {/* App shell wraps all other routes */}
      <Route element={<AppShell />}>
        {/* Public content */}
        <Route index element={<LandingPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/docs" element={<DocsPage />} />
        <Route path="/changelog" element={<ChangelogPage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/privacy" element={<LegalPage pageKey="privacy" />} />
        <Route path="/terms" element={<LegalPage pageKey="terms" />} />
        <Route path="/impressum" element={<LegalPage pageKey="impressum" />} />
        <Route path="/dpa" element={<LegalPage pageKey="dpa" />} />
        <Route path="/gdpr-rights" element={<LegalPage pageKey="gdprRights" />} />
        <Route path="/sub-processors" element={<LegalPage pageKey="subProcessors" />} />
        <Route path="/refund-policy" element={<LegalPage pageKey="refundPolicy" />} />
        <Route path="/billing-terms" element={<LegalPage pageKey="billingTerms" />} />
        <Route
          path="/accessibility-statement"
          element={<LegalPage pageKey="accessibilityStatement" />}
        />
        <Route path="/acceptable-use" element={<LegalPage pageKey="acceptableUse" />} />
        <Route path="/dmca" element={<LegalPage pageKey="dmca" />} />
        <Route path="/security" element={<LegalPage pageKey="security" />} />
        <Route path="/cookie-policy" element={<LegalPage pageKey="cookiePolicy" />} />
        <Route path="/contact" element={<LegalPage pageKey="contact" />} />
        <Route path="/device" element={<DeviceAuthPage />} />
        <Route path="/getting-started" element={<GettingStartedPage />} />

        {/* Protected — redirects to /sign-in if not authenticated */}
        <Route element={<ProtectedRoute />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route
            path="/billing"
            element={
              <Suspense fallback={null}>
                <BillingPage />
              </Suspense>
            }
          />
          <Route path="/account" element={<SettingsPage />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

export function App() {
  return (
    <>
      <AppRoutes />
      <CookieBanner />
    </>
  );
}
